/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ReactRenderer } from '@atlaskit/renderer';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAppOptionsForSelect } from '@atlassian/conversation-assistant-chat-prompt-input/use-app-options-for-select';
import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { AssignedAppsIcons } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/SourceButtonIcons';
import { encodePromptToAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { GetPromptResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';
import {
	type CreatedPromptData,
	type CreatePromptFormData,
	CreatePromptModal,
	useCreatePromptInitialData,
} from '@atlassian/rovo-conversation-actions/create-prompt-modal';
import { type PromptCard, usePromptCards } from '@atlassian/rovo-conversation-actions/prompt-cards';
import { skillExtensionHandlers } from '@atlassian/rovo-conversation-actions/prompt-cards-grid';
import {
	defaultPromptUseCaseIcon,
	getUseCaseIcon,
	promptUseCaseIconMap,
} from '@atlassian/rovo-conversation-actions/prompt-use-case-icons';
import { SharePromptModal } from '@atlassian/rovo-conversation-actions/share-prompt-modal';
import {
	type BrowseItem,
	filterBrowseItems,
	InlineBrowse,
	type InlineBrowseIconProps,
} from '@atlassian/rovo-platform-ui-components/inline-browse';
import { InlineBrowseSkeleton } from '@atlassian/rovo-platform-ui-components/inline-browse-loading';
import { useSkillMetadata } from '@atlassian/rovo-skills/services/skill-metadata-provider';

import { usePromptAppConnectors } from '../../../common/hooks/usePromptAppConnectors';
import { getIsSystemPromptForAnalytics } from '../../../common/utils/getIsSystemPromptForAnalytics';
import { useAssistanceServiceParams } from '../../../common/utils/use-assistance-service-params';
import type { ScreenType } from '../../../common/utils/use-calculated-screen-type';
import { usePromptCardSendTrackingActions } from '../../../controllers/prompt-card-send-tracking/usePromptCardSendTrackingActions';
import { useCreatePrompt } from '../../../controllers/prompts/useCreatePrompt';
import { useDeletePromptController } from '../../../controllers/prompts/useDeletePromptController';
import { usePromptActions } from '../../../controllers/prompts/usePromptActions';
import { usePromptsController } from '../../../controllers/prompts/usePromptsController';
import { insertPromptPlaceholder } from '../insertPromptPlaceholder';
import promptLibraryMessages from '../messages';
import { useStablePromptOrder } from '../useStablePromptOrder';

import { PromptLibraryModal } from './prompt-library-modal';

const styles = cssMap({
	overlay: {
		position: 'absolute',
		inset: '0',
		zIndex: 1,
		backgroundColor: token('elevation.surface'),
	},
	overlayHidden: {
		display: 'none',
	},
	/** Content we pass to HoverCard has no built-in padding/width; this wraps it. */
	hoverCardContent: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
		textAlign: 'left',
	},
	hoverCardHeader: {
		alignItems: 'center',
	},
	promptIcon: {
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},

	hoverCardAppsRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.075'),
	},
	seeAllHeaderActions: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	browseWrapper: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		overflowX: 'hidden',
	},
	browseHeaderContainer: {
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
	},
	browseHeaderRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	browseSearchBar: {
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
	},
});

/** Wrap use-case icon (no props) so it accepts InlineBrowseIconProps and render at smaller size */
const wrapUseCaseIcon =
	(IconComponent: () => React.ReactElement): React.ComponentType<InlineBrowseIconProps> =>
	() =>
		React.createElement(
			Box,
			{
				style: { width: 20, height: 20 },
			},
			React.createElement(IconComponent),
		);

/** Reuse the mapping from rovo-conversation-actions, wrapped for InlineBrowse and smaller size */
const useCaseIconMap = Object.fromEntries(
	Object.entries(promptUseCaseIconMap).map(([cat, Icon]) => [cat, wrapUseCaseIcon(Icon)]),
) as Record<string, React.ComponentType<InlineBrowseIconProps>>;

const defaultPromptIcon = wrapUseCaseIcon(defaultPromptUseCaseIcon);

const promptParagraphNodeComponents = {
	paragraph: ({ children }: { children: React.ReactNode }) => (
		<Text size="small" color="color.text.subtle">
			{children}
		</Text>
	),
};

function getRawPromptsFromResponse(response: unknown): GetPromptResponse[] {
	if (!response || typeof response !== 'object') {
		return [];
	}
	const r = response as { prompts?: GetPromptResponse[]; items?: GetPromptResponse[] };
	return r.prompts ?? r.items ?? [];
}

export type SidebarPromptInlineBrowseProps = {
	screenType: ScreenType;
	conversationId: string;
	closeInlineBrowse: () => void;
};

export const SidebarPromptInlineBrowse = ({
	screenType,
	conversationId,
	closeInlineBrowse,
}: SidebarPromptInlineBrowseProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { promptCards: defaultPromptCards } = usePromptCards();
	const { insertPlaceholder } = useEditorPlaceholder();
	const { setPromptCardSource } = usePromptCardSendTrackingActions();
	const { setReasoningMode } = useChatModeStoreActions();
	const { organization } = useProfileController();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'sidebarPromptInlineBrowse',
	});
	const { handlePromptAppConnectors } = usePromptAppConnectors({
		conversationId,
		assistanceServiceParams,
	});
	const { sendAnalyticsEvent } = useAnalytics();
	const { options: appOptions } = useAppOptionsForSelect(assistanceServiceParams);
	const {
		data: promptsResponse,
		isFetching,
		refetch: refetchPrompts,
		updatePromptFavoriteInCache,
		bulkUpdatePromptFavoritesInCache,
	} = usePromptsController({
		assistanceServiceParams,
		enabled: FeatureGates.getExperimentValue('rovo_chat_prompt_library_v2_exp', 'isEnabled', false),
	});
	// Show skeleton while My Prompts are loading
	const hasSettledRef = useRef(false);
	if (!isFetching) {
		hasSettledRef.current = true;
	}
	const isPromptsLoading = !hasSettledRef.current;
	const { handleCreatePrompt } = useCreatePrompt({
		assistanceServiceParams,
		refetchPrompts,
	});
	const { deletePrompt } = useDeletePromptController({
		assistanceServiceParams,
	});

	const {
		handleFavoriteToggle: handleModalFavoriteToggle,
		handleBulkFavorite: handleModalBulkFavorite,
		handleBulkDelete: handleModalBulkDelete,
		handleBulkShare: handleModalBulkShare,
	} = usePromptActions({
		assistanceServiceParams,
		showErrorFlag: true,
		deletePrompt,
		refetchPrompts,
		updatePromptFavoriteInCache,
		bulkUpdatePromptFavoritesInCache,
	});

	const providerMetadata = useSkillMetadata();
	const skillMetadataMap = useMemo(
		() => providerMetadata?.skillMetadataMap ?? {},
		[providerMetadata],
	);

	const [isCreatePromptModalOpen, setIsCreatePromptModalOpen] = useState(false);
	const [isSeeAllModalOpen, setIsSeeAllModalOpen] = useState(false);
	const [isSharePromptModalOpen, setIsSharePromptModalOpen] = useState(false);
	const [createdPromptData, setCreatedPromptData] = useState<CreatedPromptData | null>(null);
	const [editingPromptCard, setEditingPromptCard] = useState<PromptCard | null>(null);
	const [duplicatingPromptCard, setDuplicatingPromptCard] = useState<PromptCard | null>(null);
	const [createModalIntent, setCreateModalIntent] = useState<
		'create' | 'edit' | 'duplicate' | null
	>(null);

	const handleCreatePromptSubmit = useCallback(
		async (data: CreatePromptFormData) => {
			const cardToDeleteAfterCreate = editingPromptCard;
			const result = await handleCreatePrompt(data);
			if (cardToDeleteAfterCreate) {
				await deletePrompt(cardToDeleteAfterCreate.id);
				await refetchPrompts();
				setEditingPromptCard(null);
			}
			setDuplicatingPromptCard(null);
			return {
				...data,
				shareId: result.id,
				ownerName: undefined,
			};
		},
		[handleCreatePrompt, editingPromptCard, deletePrompt, refetchPrompts],
	);

	const handleCreateSuccess = useCallback(
		(promptData: CreatedPromptData) => {
			if (createModalIntent === 'create') {
				sendAnalyticsEvent({
					action: 'created',
					actionSubject: 'prompt',
					actionSubjectId: 'promptCreated',
				});
			} else if (createModalIntent === 'edit') {
				sendAnalyticsEvent({
					action: 'edited',
					actionSubject: 'prompt',
					actionSubjectId: 'promptEdited',
					attributes: {
						isSystemPrompt: getIsSystemPromptForAnalytics(editingPromptCard?.isUserGenerated),
					},
				});
			} else if (createModalIntent === 'duplicate') {
				sendAnalyticsEvent({
					action: 'duplicated',
					actionSubject: 'prompt',
					actionSubjectId: 'promptDuplicated',
					attributes: {
						isSystemPrompt: getIsSystemPromptForAnalytics(duplicatingPromptCard?.isUserGenerated),
					},
				});
			}
			setCreateModalIntent(null);
			setCreatedPromptData(promptData);
			setIsCreatePromptModalOpen(false);
			setIsSharePromptModalOpen(true);
		},
		[createModalIntent, sendAnalyticsEvent, editingPromptCard, duplicatingPromptCard],
	);

	const handleCloseShare = useCallback(() => {
		setIsSharePromptModalOpen(false);
		setCreatedPromptData(null);
	}, []);

	const handleSharePrompt = useCallback(
		(card: PromptCard) => {
			// Convert useCase string to SelectOption
			const useCaseOption = card.useCase ? { label: card.useCase, value: card.useCase } : null;

			// Convert roles to SelectOption[]
			const roleSelectOptions = (card.roles || []).map((role) => ({
				label: role,
				value: role,
			}));

			// Convert appIds to SelectOption[] using appOptions
			const appSelectOptions = (card.appIds || []).map((id) => {
				const found = appOptions?.find((opt) => opt.value === id);
				return found || { label: id, value: id };
			});

			setCreatedPromptData({
				title: card.title,
				prompt: card.prompt,
				shareId: card.id,
				ownerName: undefined,
				useCase: useCaseOption,
				role: roleSelectOptions,
				app: appSelectOptions,
			});
			setIsSharePromptModalOpen(true);
		},
		[appOptions],
	);

	const handleEditPrompt = useCallback((card: PromptCard) => {
		setIsSeeAllModalOpen(false);
		setCreateModalIntent('edit');
		setEditingPromptCard(card);
		setDuplicatingPromptCard(null);
		// setTimeout(0) allows the "See All" modal to close before opening the create modal,
		// preventing modal stacking/layering issues in the Atlassian modal system.
		setTimeout(() => setIsCreatePromptModalOpen(true), 0);
	}, []);

	const handleDuplicatePrompt = useCallback((card: PromptCard) => {
		setIsSeeAllModalOpen(false);
		setCreateModalIntent('duplicate');
		setDuplicatingPromptCard(card);
		setEditingPromptCard(null);
		// setTimeout(0) allows the "See All" modal to close before opening the create modal,
		// preventing modal stacking/layering issues in the Atlassian modal system.
		setTimeout(() => setIsCreatePromptModalOpen(true), 0);
	}, []);

	const createPromptInitialData = useCreatePromptInitialData({
		editingPromptCard,
		duplicatingPromptCard,
		appOptions: appOptions ?? [],
	});

	const handleCreatePromptModalClose = useCallback(() => {
		setIsCreatePromptModalOpen(false);
		setEditingPromptCard(null);
		setDuplicatingPromptCard(null);
		setCreateModalIntent(null);
	}, []);

	const handleOpenCreatePromptModal = useCallback(() => {
		setCreateModalIntent('create');
		setIsCreatePromptModalOpen(true);
	}, []);

	const handleOpenSeeAllModal = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'seeAllModal',
		});
		setIsSeeAllModalOpen(true);
	}, [sendAnalyticsEvent]);

	const handleEmptyStateViewed = useCallback(
		(emptyStateType: 'emptyFavorites' | 'emptyMyPrompts' | 'noPromptsFromFilters') => {
			sendAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'emptyState',
				actionSubjectId: 'promptLibraryEmptyState',
				attributes: {
					emptyStateType,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	// Order: (1) favorited prompts, (2) my prompts, (3) default prompts, (4) 3p app prompts
	// Use stable ordering to prevent list from jumping when favoriting
	const rawPrompts = useMemo(() => getRawPromptsFromResponse(promptsResponse), [promptsResponse]);
	const allPromptsFromApi = useStablePromptOrder(rawPrompts);

	const myPromptCards = useMemo(
		() => allPromptsFromApi.filter((card) => card.isUserGenerated === true),
		[allPromptsFromApi],
	);

	const promptCards = useMemo(
		() => [
			...allPromptsFromApi,
			// In v2, default prompts are pre-seeded in the database and returned via the API,
			// so we don't need to include the hardcoded prompts to avoid duplication
			...(FeatureGates.getExperimentValue('rovo_chat_prompt_library_v2_exp', 'isEnabled', false)
				? []
				: defaultPromptCards),
		],
		[allPromptsFromApi, defaultPromptCards],
	);

	const cardById = useMemo(
		() => new Map(promptCards.map((card) => [card.id, card])),
		[promptCards],
	);

	const handlePromptSelect = useCallback(
		(card: PromptCard) => {
			const promptSource =
				myPromptCards.some((c) => c.id === card.id) && card.isUserGenerated
					? 'myPrompts'
					: 'default';
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'promptCardClick',
				attributes: {
					cardId: card.id,
					useCase: card.useCase,
					roles: card.roles,
					enableDeepResearch: card.enableDeepResearch,
					isFavorited: card.isFavorited ?? false,
					promptSource,
				},
			});
			if (card.enableDeepResearch) {
				setReasoningMode(conversationId, 'deep-research');
			} else {
				setReasoningMode(conversationId, 'auto');
			}
			setPromptCardSource({
				cardId: card.id,
				prompt: card.prompt,
				promptSource,
				isFavorited: card.isFavorited,
			});
			handlePromptAppConnectors(card.appIds, { forceMultiline: true });
			insertPromptPlaceholder(card.prompt, insertPlaceholder, skillMetadataMap);
		},
		[
			conversationId,
			handlePromptAppConnectors,
			insertPlaceholder,
			myPromptCards,
			sendAnalyticsEvent,
			setPromptCardSource,
			setReasoningMode,
			skillMetadataMap,
		],
	);

	const browseItems: BrowseItem[] = useMemo(
		() =>
			promptCards.map((card) => ({
				id: card.id,
				name: card.title,
				description: card.prompt,
				product: card.useCase,
				iconName: card.useCase,
				appIds: card.appIds,
				roles: card.roles,
			})),
		[promptCards],
	);

	const [searchValue, setSearchValue] = useState('');
	const filteredBrowseItems = useMemo(
		() => filterBrowseItems(browseItems, searchValue),
		[browseItems, searchValue],
	);

	const handleSelectBrowseItem = useCallback(
		(browseItem: BrowseItem) => {
			const card = cardById.get(browseItem.id);
			if (!card) {
				return;
			}
			handlePromptSelect(card);
			closeInlineBrowse();
		},
		[cardById, closeInlineBrowse, handlePromptSelect],
	);

	const handleModalCardClick = useCallback(
		(card: PromptCard) => {
			handlePromptSelect(card);
			setIsSeeAllModalOpen(false);
			closeInlineBrowse();
		},
		[closeInlineBrowse, handlePromptSelect],
	);

	const handleModalDeletePrompt = useCallback(
		async (promptId: string) => {
			// Look up the card to get isUserGenerated for analytics before deleting
			const card = promptCards.find((c) => c.id === promptId);
			await deletePrompt(promptId);
			await refetchPrompts();
			sendAnalyticsEvent({
				action: 'deleted',
				actionSubject: 'prompt',
				actionSubjectId: 'promptDeleted',
				attributes: {
					isSystemPrompt: getIsSystemPromptForAnalytics(card?.isUserGenerated),
				},
			});
		},
		[deletePrompt, refetchPrompts, sendAnalyticsEvent, promptCards],
	);

	const renderHoverCardContent = useCallback(
		(browseItem: BrowseItem) => {
			const card = cardById.get(browseItem.id);
			if (!card) {
				return null;
			}
			const Icon = getUseCaseIcon(card.useCase);
			const effectiveAppIds = card.appIds ?? [];
			return (
				<Box xcss={styles.hoverCardContent}>
					<Stack space="space.100">
						<Inline space="space.100" xcss={styles.hoverCardHeader}>
							<Box xcss={styles.promptIcon}>
								<Icon />
							</Box>
							<Heading size="xsmall">{browseItem.name}</Heading>
						</Inline>
						<Box>
							<ReactRenderer
								document={encodePromptToAdf(browseItem.description ?? '', skillMetadataMap).adf}
								extensionHandlers={skillExtensionHandlers}
								nodeComponents={promptParagraphNodeComponents}
							/>
						</Box>
						{effectiveAppIds.length > 0 && assistanceServiceParams && (
							<Box xcss={styles.hoverCardAppsRow} paddingBlockStart="space.100">
								<AssignedAppsIcons
									appIds={effectiveAppIds}
									assistanceServiceParams={assistanceServiceParams}
								/>
							</Box>
						)}
					</Stack>
				</Box>
			);
		},
		[cardById, assistanceServiceParams, skillMetadataMap],
	);

	return (
		<Box xcss={cx(styles.overlay, screenType !== 'prompt-library' && styles.overlayHidden)}>
			<Suspense
				interactionName="rovo-conversation-assistant-prompt-library-inline-browse"
				fallback={<Spinner />}
			>
				<ErrorBoundary
					fallbackRender={() => <ChatGenericError errorType="generic" />}
					onError={(error) => {
						logException({
							exception: error,
							name: 'sidebarPromptInlineBrowse',
							tags: { conversationId },
						});
					}}
				>
					{isPromptsLoading ? (
						<Box xcss={styles.browseWrapper}>
							<Box xcss={styles.browseHeaderContainer}>
								<Box xcss={styles.browseHeaderRow}>
									<Heading size="xlarge">
										{formatMessage(promptLibraryMessages.headerTitle)}
									</Heading>
									<Inline xcss={styles.seeAllHeaderActions}>
										<Button appearance="primary" onClick={handleOpenCreatePromptModal}>
											{formatMessage(promptLibraryMessages.createPrompt)}
										</Button>
										<Button appearance="default" onClick={handleOpenSeeAllModal}>
											{formatMessage(promptLibraryMessages.seeAll)}
										</Button>
									</Inline>
								</Box>
							</Box>
							<Box xcss={styles.browseSearchBar}>
								<SearchInput
									placeholderText={formatMessage(promptLibraryMessages.browseSearchPlaceholder)}
									onSearch={() => {}}
									isCompact
									isDisabled
								/>
							</Box>
							<InlineBrowseSkeleton />
						</Box>
					) : (
						<Box xcss={styles.browseWrapper}>
							<Box xcss={styles.browseHeaderContainer}>
								<Box xcss={styles.browseHeaderRow}>
									<Heading size="xlarge">
										{formatMessage(promptLibraryMessages.headerTitle)}
									</Heading>
									<Inline xcss={styles.seeAllHeaderActions}>
										<Button appearance="primary" onClick={handleOpenCreatePromptModal}>
											{formatMessage(promptLibraryMessages.createPrompt)}
										</Button>
										<Button appearance="default" onClick={handleOpenSeeAllModal}>
											{formatMessage(promptLibraryMessages.seeAll)}
										</Button>
									</Inline>
								</Box>
							</Box>
							<Box xcss={styles.browseSearchBar}>
								<SearchInput
									placeholderText={formatMessage(promptLibraryMessages.browseSearchPlaceholder)}
									onSearch={setSearchValue}
									isCompact
									autoFocus
								/>
							</Box>
							<InlineBrowse
								browseItems={filteredBrowseItems}
								onSelectBrowseItem={handleSelectBrowseItem}
								onClose={closeInlineBrowse}
								iconMap={useCaseIconMap}
								defaultIcon={defaultPromptIcon}
								renderHoverCardContent={renderHoverCardContent}
							/>
						</Box>
					)}
					<CreatePromptModal
						appOptions={appOptions?.length ? appOptions : undefined}
						initialData={createPromptInitialData}
						isEditMode={!!editingPromptCard}
						isOpen={isCreatePromptModalOpen}
						onClose={handleCreatePromptModalClose}
						onCreate={handleCreatePromptSubmit}
						onCreateSuccess={handleCreateSuccess}
					/>
					{createdPromptData && (
						<SharePromptModal
							isOpen={isSharePromptModalOpen}
							onClose={handleCloseShare}
							promptData={createdPromptData}
							organizationName={organization}
						/>
					)}
					<PromptLibraryModal
						isOpen={isSeeAllModalOpen}
						onClose={() => setIsSeeAllModalOpen(false)}
						promptCards={promptCards}
						myPromptCards={myPromptCards}
						onCardSelect={handleModalCardClick}
						isLoading={isPromptsLoading}
						onCreatePromptClick={() => {
							setIsSeeAllModalOpen(false);
							setEditingPromptCard(null);
							setDuplicatingPromptCard(null);
							setCreateModalIntent('create');
							// setTimeout(0) allows the "See All" modal to close before opening the create modal,
							// preventing modal stacking/layering issues in the Atlassian modal system.
							setTimeout(() => setIsCreatePromptModalOpen(true), 0);
						}}
						onDeletePrompt={handleModalDeletePrompt}
						onBulkDelete={handleModalBulkDelete}
						onEditPrompt={handleEditPrompt}
						onDuplicatePrompt={handleDuplicatePrompt}
						onFavoriteToggle={handleModalFavoriteToggle}
						onBulkFavorite={handleModalBulkFavorite}
						onBulkShare={handleModalBulkShare}
						onSharePrompt={handleSharePrompt}
						assistanceServiceParams={assistanceServiceParams}
						skillMetadata={skillMetadataMap}
						appOptions={appOptions}
						onEmptyStateViewed={handleEmptyStateViewed}
					/>
				</ErrorBoundary>
			</Suspense>
		</Box>
	);
};
